/**
 * useOnOutsideClick.tsx
 * Hook to alert when a user clicks outside of a ref
 */

import { useCallback, useEffect } from "react";
import { usePortalContainer } from "../context/PortalContext/usePortalContext";

type Ref = React.RefObject<HTMLElement>;
/**
 * Custom React hook that triggers a callback function when a click event occurs outside the specified element.
 *
 * @param {React.RefObject<HTMLElement>} ref - A React ref object pointing to the element to detect outside clicks for.
 * @param {Function} callback - A callback function to be executed when a click outside the referenced element is detected.
 */
export default function useOnOutsideClick(
  ...args: [...ref: Ref[], callback: () => void]
) {
  // Extract the last argument as the callback function
  const callback = args.pop() as () => void;
  const refs = args as Ref[];

  // Portal container to listen for clicks inside popout windows
  const portalContainer = usePortalContainer();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      // If event target is inside any of the referenced elements, do nothing
      const isInsideAny = refs.some(
        (ref) => ref.current && ref.current.contains(event.target as Node)
      );

      if (!isInsideAny) {
        callback();
      }
    },
    [refs, callback]
  );

  useEffect(() => {
    document.addEventListener("click", handleClick);
    portalContainer?.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
      portalContainer?.removeEventListener("click", handleClick);
    };
  }, [refs, callback, handleClick, portalContainer]);
}
